import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bell, Menu, Moon, Sun, X } from "lucide-react";
import { alerts } from "./data";
import { useTheme } from "./ThemeProvider";

const links = [
  { label: "Engines", href: "#engines" },
  { label: "Execution", href: "#execution" },
  { label: "News", href: "#news" },
  { label: "Performance", href: "#performance" },
  { label: "Terminal", href: "#terminal" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-5 py-3 lg:px-8">
        <a href="#top" className="flex min-w-0 items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-primary/40 font-mono text-sm font-bold text-primary">
            D
          </span>
          <span className="truncate text-sm font-semibold tracking-[0.18em] uppercase">
            Daddy <span className="text-gold-gradient">Of AI</span>
          </span>
        </a>

        <div className="flex items-center gap-2">
          <nav className="mr-2 hidden items-center gap-7 lg:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-xs font-medium tracking-wide text-muted-foreground uppercase transition-colors hover:text-primary"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <button
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border/70 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            <motion.span
              key={theme}
              initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="grid place-items-center"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </motion.span>
          </button>

          <button
            onClick={() => setFeed((v) => !v)}
            aria-label="Notifications"
            className="relative grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border/70 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            <Bell className="h-4 w-4" />
            <span className="live-dot absolute top-1.5 right-1.5" />
          </button>

          <a
            href="#terminal"
            className="hidden rounded-lg px-4 py-2 text-xs font-semibold tracking-wide text-primary-foreground uppercase sm:block"
            style={{ backgroundImage: "var(--gradient-gold)" }}
          >
            Enter Terminal
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border/70 lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="glass border-t border-border/60 px-5 py-4 lg:hidden">
          <div className="flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm text-muted-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {feed && (
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          className="glass absolute top-16 right-4 w-[min(22rem,calc(100vw-2rem))] rounded-xl p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase">Live Feed</p>
            <span className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase">
              <span className="live-dot" /> Streaming
            </span>
          </div>
          <div className="space-y-2">
            {alerts.map((a, i) => (
              <motion.div
                key={a.text}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="rounded-lg border border-border/60 bg-surface/60 p-3"
              >
                <div className="flex items-center justify-between text-[11px]">
                  <span
                    className={
                      a.tone === "bull"
                        ? "text-bull"
                        : a.tone === "bear"
                          ? "text-bear"
                          : "text-warn"
                    }
                  >
                    {a.kind}
                  </span>
                  <span className="text-muted-foreground">{a.ago}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{a.text}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-muted-foreground">
            Mirrored to Telegram alerts &amp; push notifications.
          </p>
        </motion.div>
      )}
    </header>
  );
}
